"use strict";
(() => {
  // src/scripts/domain/theme-state.ts
  var defaultThemeState = () => ({
    lastVisitedPath: window.location.pathname,
    whatsappClicks: 0
  });

  // src/scripts/infrastructure/browser-state.ts
  var storageKey = "siba:theme-state";
  var readThemeState = () => {
    try {
      const rawState = window.sessionStorage.getItem(storageKey);
      return rawState ? { ...defaultThemeState(), ...JSON.parse(rawState) } : defaultThemeState();
    } catch (e) {
      return defaultThemeState();
    }
  };
  var writeThemeState = (state) => {
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(state));
    } catch (e) {
    }
  };

  // src/scripts/application/theme-state-service.ts
  var rememberCurrentPath = () => {
    const state = readThemeState();
    writeThemeState({ ...state, lastVisitedPath: window.location.pathname });
  };
  var rememberOpenedFaq = (question) => {
    const state = readThemeState();
    writeThemeState({ ...state, lastOpenedFaq: question });
  };
  var countWhatsappClick = () => {
    const state = readThemeState();
    writeThemeState({ ...state, whatsappClicks: state.whatsappClicks + 1 });
  };

  // src/scripts/infrastructure/observability.ts
  var DEFAULT_SAMPLE_RATE = 100;
  var MAX_TEXT_LENGTH = 120;
  var getConfig = () => {
    var _a, _b, _c, _d, _e;
    const config = (_a = window.SIBA_OBSERVABILITY) != null ? _a : {};
    return {
      ...config,
      enabled: (_b = config.enabled) != null ? _b : true,
      sampleRate: normalizeSampleRate(config.sampleRate),
      debug: (_c = config.debug) != null ? _c : false,
      captureInteractions: (_d = config.captureInteractions) != null ? _d : true,
      capturePerformance: (_e = config.capturePerformance) != null ? _e : true
    };
  };
  var normalizeSampleRate = (value) => {
    if (typeof value !== "number" || Number.isNaN(value)) return DEFAULT_SAMPLE_RATE;
    return Math.max(0, Math.min(100, value));
  };
  var shouldSample = (sampleRate) => {
    if (sampleRate >= 100) return true;
    if (sampleRate <= 0) return false;
    return Math.random() * 100 < sampleRate;
  };
  var truncate = (value, maxLength = MAX_TEXT_LENGTH) => {
    const normalized = value.replace(/\s+/g, " ").trim();
    return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 1)}...` : normalized;
  };
  var sanitizeUrl = (value) => {
    if (!value) return "";
    try {
      const url = new URL(value, window.location.origin);
      return `${url.origin === window.location.origin ? "" : url.origin}${url.pathname}`;
    } catch (e) {
      return "";
    }
  };
  var getElementLabel = (element) => {
    var _a, _b, _c, _d;
    const explicitLabel = (_a = element.getAttribute("aria-label")) != null ? _a : element.getAttribute("data-observability-label");
    if (explicitLabel) return truncate(explicitLabel);
    return truncate((_d = (_c = (_b = element.textContent) != null ? _b : element.getAttribute("title")) != null ? _c : element.id) != null ? _d : element.className.toString());
  };
  var createPayload = (event, severity, detail) => {
    const config = getConfig();
    return {
      event,
      severity,
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      page: {
        path: `${window.location.pathname}${window.location.search ? "?..." : ""}`,
        title: document.title,
        referrer: sanitizeUrl(document.referrer),
        locale: config.locale,
        pageType: config.pageType,
        template: config.template
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      theme: config.theme,
      release: config.release,
      detail
    };
  };
  var sendToEndpoint = (endpoint, payload) => {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const sent = navigator.sendBeacon(endpoint, new Blob([body], { type: "application/json" }));
      if (sent) return;
    }
    void fetch(endpoint, {
      body,
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      method: "POST"
    }).catch(() => {
    });
  };
  var trackObservabilityEvent = (event, detail, severity = "info") => {
    var _a;
    const config = getConfig();
    if (!config.enabled || !shouldSample(config.sampleRate)) return;
    const payload = createPayload(event, severity, detail);
    const observabilityWindow = window;
    observabilityWindow.dataLayer = (_a = observabilityWindow.dataLayer) != null ? _a : [];
    observabilityWindow.dataLayer.push({
      event: `siba_${event}`,
      siba: payload
    });
    window.dispatchEvent(new CustomEvent("siba:observability", { detail: payload }));
    if (config.endpoint) sendToEndpoint(config.endpoint, payload);
    if (config.debug) console.info("[SIBA observability]", payload);
  };
  var normalizeError = (error) => {
    if (error instanceof Error) {
      return {
        message: truncate(error.message, 240),
        name: error.name,
        stack: error.stack ? truncate(error.stack, 900) : void 0
      };
    }
    return {
      message: truncate(String(error), 240)
    };
  };
  var captureGlobalErrors = () => {
    window.addEventListener("error", (event) => {
      var _a;
      trackObservabilityEvent(
        "frontend_error",
        {
          ...normalizeError((_a = event.error) != null ? _a : event.message),
          filename: sanitizeUrl(event.filename),
          line: event.lineno,
          column: event.colno
        },
        "error"
      );
    });
    window.addEventListener("unhandledrejection", (event) => {
      trackObservabilityEvent("unhandled_rejection", normalizeError(event.reason), "error");
    });
  };
  var getInteraction = (target) => {
    const link = target.closest("a[href]");
    const button = target.closest("button, summary, [role='button']");
    if (link == null ? void 0 : link.matches(".siba-whatsapp-float, a[href*='wa.me'], a[href*='api.whatsapp.com']")) {
      return { type: "whatsapp_click", label: getElementLabel(link), href: sanitizeUrl(link.href) };
    }
    if (link == null ? void 0 : link.matches(".header__menu-item, .menu-drawer__menu-item")) {
      return { type: "navigation_click", label: getElementLabel(link), href: sanitizeUrl(link.href) };
    }
    if (button == null ? void 0 : button.matches(".header__icon--menu, summary.header__icon--menu")) {
      return { type: "menu_toggle", label: getElementLabel(button) };
    }
    if (button == null ? void 0 : button.matches(".header__icon--search, summary[aria-haspopup='dialog']")) {
      return { type: "search_open", label: getElementLabel(button) };
    }
    if (button == null ? void 0 : button.matches("[name='add'], [data-siba-add-to-cart] button[type='submit']")) {
      return { type: "add_to_cart_submit", label: getElementLabel(button) };
    }
    return null;
  };
  var captureInteractions = () => {
    document.addEventListener(
      "click",
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        const interaction = getInteraction(target);
        if (!interaction) return;
        trackObservabilityEvent("interaction", interaction);
      },
      { capture: true }
    );
  };
  var capturePerformance = () => {
    window.addEventListener("load", () => {
      window.setTimeout(() => {
        const navigation = performance.getEntriesByType("navigation")[0];
        if (!navigation) return;
        trackObservabilityEvent("page_performance", {
          domContentLoadedMs: Math.round(navigation.domContentLoadedEventEnd),
          loadMs: Math.round(navigation.loadEventEnd),
          responseMs: Math.round(navigation.responseEnd - navigation.requestStart),
          transferSize: navigation.transferSize
        });
      }, 0);
    });
    if (!("PerformanceObserver" in window)) return;
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === "largest-contentful-paint") {
            trackObservabilityEvent("largest_contentful_paint", { valueMs: Math.round(entry.startTime) });
          }
        }
      });
      observer.observe({ type: "largest-contentful-paint", buffered: true });
    } catch (e) {
    }
  };
  var initObservability = () => {
    const config = getConfig();
    if (!config.enabled) return;
    captureGlobalErrors();
    if (config.captureInteractions) captureInteractions();
    if (config.capturePerformance) capturePerformance();
    trackObservabilityEvent("theme_boot", {
      hasEndpoint: Boolean(config.endpoint),
      captureInteractions: config.captureInteractions,
      capturePerformance: config.capturePerformance
    });
  };

  // src/scripts/lib/dom.ts
  var onReady = (callback) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, { once: true });
      return;
    }
    callback();
  };
  var queryAll = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  // src/scripts/presentation/ai-chat.ts
  var storageKey2 = "siba_ai_chat_conversation_id";
  var createBubble = (text, role) => {
    const bubble = document.createElement("div");
    bubble.className = `siba-ai-chat__message siba-ai-chat__message--${role}`;
    bubble.textContent = text;
    return bubble;
  };
  var scrollToLatest = (messages) => {
    messages.scrollTop = messages.scrollHeight;
  };
  var initAiChat = () => {
    queryAll("[data-siba-ai-chat]").forEach((root) => {
      var _a;
      const endpoint = (_a = root.dataset.endpoint) == null ? void 0 : _a.trim();
      if (!endpoint) return;
      const toggle = root.querySelector("[data-siba-ai-chat-toggle]");
      const close = root.querySelector("[data-siba-ai-chat-close]");
      const panel = root.querySelector("[data-siba-ai-chat-panel]");
      const form = root.querySelector("[data-siba-ai-chat-form]");
      const input = root.querySelector("[data-siba-ai-chat-input]");
      const messages = root.querySelector("[data-siba-ai-chat-messages]");
      if (!toggle || !close || !panel || !form || !input || !messages) return;
      const setOpen = (open) => {
        root.classList.toggle("is-open", open);
        toggle.setAttribute("aria-expanded", String(open));
        panel.toggleAttribute("hidden", !open);
        if (open) input.focus();
      };
      toggle.addEventListener("click", () => setOpen(!root.classList.contains("is-open")));
      close.addEventListener("click", () => setOpen(false));
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        const message = input.value.trim();
        if (!message) return;
        input.value = "";
        messages.appendChild(createBubble(message, "user"));
        const pending = createBubble("Checking SIBA information...", "assistant");
        messages.appendChild(pending);
        scrollToLatest(messages);
        const conversationId = Number(window.sessionStorage.getItem(storageKey2) || "0");
        try {
          const response = await fetch(`${endpoint.replace(/\/$/, "")}/api/chat`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message, conversation_id: conversationId })
          });
          const data = await response.json();
          if (data.conversation_id) {
            window.sessionStorage.setItem(storageKey2, String(data.conversation_id));
          }
          pending.textContent = data.answer || data.error || "I could not answer that yet.";
        } catch (_error) {
          pending.textContent = "The SIBA assistant is not available right now.";
        }
        scrollToLatest(messages);
      });
    });
  };

  // src/scripts/presentation/cart.ts
  var getSectionInnerHTML = (html, selector = ".shopify-section") => {
    var _a;
    const element = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
    return (_a = element == null ? void 0 : element.innerHTML) != null ? _a : "";
  };
  var renderFallbackCartSections = (sections) => {
    const drawerHtml = sections["cart-drawer"];
    const cartIconHtml = sections["cart-icon-bubble"];
    if (drawerHtml) {
      const drawer = document.querySelector("#CartDrawer");
      if (drawer) drawer.innerHTML = getSectionInnerHTML(drawerHtml, "#CartDrawer");
    }
    if (cartIconHtml) {
      const cartIcon = document.querySelector("#cart-icon-bubble");
      if (cartIcon) cartIcon.innerHTML = getSectionInnerHTML(cartIconHtml);
    }
  };
  var parseCartResponse = async (response) => {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error(response.ok ? "Unexpected cart response" : `Cart request failed (${response.status})`);
    }
  };
  var initSibaCartForms = () => {
    document.addEventListener("submit", async (event) => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i;
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!(form == null ? void 0 : form.matches("[data-siba-add-to-cart]"))) return;
      event.preventDefault();
      const button = form.querySelector("button[type='submit']");
      const previousLabel = (_a = button == null ? void 0 : button.textContent) != null ? _a : "";
      if (button) {
        button.disabled = true;
        button.textContent = "Adding";
      }
      try {
        const cartDrawer = document.querySelector("cart-drawer");
        const formData = new FormData(form);
        const sections = (_c = (_b = cartDrawer == null ? void 0 : cartDrawer.getSectionsToRender) == null ? void 0 : _b.call(cartDrawer).map((section) => section.id)) != null ? _c : [
          "cart-drawer",
          "cart-icon-bubble"
        ];
        formData.append("sections", sections.join(","));
        formData.append("sections_url", window.location.pathname);
        (_d = cartDrawer == null ? void 0 : cartDrawer.setActiveElement) == null ? void 0 : _d.call(cartDrawer, button != null ? button : form);
        const shopifyWindow = window;
        const cartAddUrl = (_f = (_e = shopifyWindow.routes) == null ? void 0 : _e.cart_add_url) != null ? _f : form.action;
        const response = await fetch(cartAddUrl, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          body: formData
        });
        const parsedState = await parseCartResponse(response);
        if (!response.ok) throw new Error((_g = parsedState == null ? void 0 : parsedState.description) != null ? _g : "Unable to add product");
        if (cartDrawer == null ? void 0 : cartDrawer.renderContents) {
          cartDrawer.classList.remove("is-empty");
          cartDrawer.renderContents(parsedState);
        } else {
          renderFallbackCartSections((_h = parsedState.sections) != null ? _h : {});
          cartDrawer == null ? void 0 : cartDrawer.classList.remove("is-empty");
          (_i = cartDrawer == null ? void 0 : cartDrawer.open) == null ? void 0 : _i.call(cartDrawer, button != null ? button : form);
        }
      } catch (error) {
        if (button) button.textContent = "Try again";
        console.error(error);
        return;
      } finally {
        if (button) {
          window.setTimeout(() => {
            button.disabled = false;
            button.textContent = previousLabel;
          }, 900);
        }
      }
    });
  };

  // src/scripts/presentation/collection-filters.ts
  var initialState = {
    need: "all",
    format: "all"
  };
  var needAliases = {
    balance: "calm",
    calma: "calm",
    calm: "calm",
    diario: "wellness",
    easy: "easy",
    energy: "energy",
    energia: "energy",
    format: "easy",
    gummies: "easy",
    rutina: "wellness",
    vitality: "energy",
    wellness: "wellness"
  };
  var normalizeNeed = (value) => {
    var _a;
    const normalized = (value != null ? value : "").trim().toLowerCase();
    return (_a = needAliases[normalized]) != null ? _a : "all";
  };
  var readInitialState = () => {
    var _a, _b;
    const params = new URLSearchParams(window.location.search);
    return {
      need: normalizeNeed((_a = params.get("need")) != null ? _a : params.get("objetivo")),
      format: (_b = params.get("format")) != null ? _b : "all"
    };
  };
  var tokenList = (value) => (value != null ? value : "").split(/\s+/).map((token) => token.trim()).filter(Boolean);
  var matches = (card, state) => {
    var _a;
    const needs = tokenList(card.dataset.sibaNeed);
    const format = (_a = card.dataset.sibaFormat) != null ? _a : "";
    const needMatches = state.need === "all" || needs.includes(state.need);
    const formatMatches = state.format === "all" || format === state.format;
    return needMatches && formatMatches;
  };
  var initCollectionFilters = () => {
    queryAll("[data-siba-collection]").forEach((root) => {
      const state = { ...initialState, ...readInitialState() };
      const buttons = queryAll("[data-siba-filter-group][data-siba-filter-value]", root);
      const cards = queryAll("[data-siba-collection-card]", root);
      const openButton = root.querySelector("[data-siba-filters-open]");
      const closeButton = root.querySelector("[data-siba-filters-close]");
      const overlay = root.querySelector("[data-siba-filters-overlay]");
      const activeFilters = root.querySelector("[data-siba-active-filters]");
      const count = root.querySelector("[data-siba-visible-count]");
      const empty = root.querySelector("[data-siba-empty]");
      if (!buttons.length || !cards.length) return;
      const setFiltersOpen = (isOpen) => {
        root.classList.toggle("is-filters-open", isOpen);
        openButton == null ? void 0 : openButton.setAttribute("aria-expanded", String(isOpen));
      };
      openButton == null ? void 0 : openButton.addEventListener("click", () => setFiltersOpen(true));
      closeButton == null ? void 0 : closeButton.addEventListener("click", () => setFiltersOpen(false));
      overlay == null ? void 0 : overlay.addEventListener("click", () => setFiltersOpen(false));
      root.addEventListener("keydown", (event) => {
        if (event.key === "Escape") setFiltersOpen(false);
      });
      const render = () => {
        let visibleCount = 0;
        cards.forEach((card) => {
          const isVisible = matches(card, state);
          card.hidden = !isVisible;
          if (isVisible) visibleCount += 1;
        });
        if (count) count.textContent = String(visibleCount);
        if (empty) empty.hidden = visibleCount > 0;
      };
      const getFilterLabel = (group) => {
        var _a, _b;
        const activeButton = buttons.find(
          (candidate) => candidate.dataset.sibaFilterGroup === group && candidate.dataset.sibaFilterValue === state[group]
        );
        return (_b = (_a = activeButton == null ? void 0 : activeButton.textContent) == null ? void 0 : _a.trim()) != null ? _b : state[group];
      };
      const renderActiveFilters = () => {
        if (!activeFilters) return;
        const activeGroups = Object.keys(state).filter((group) => state[group] !== "all");
        activeFilters.replaceChildren();
        activeFilters.hidden = activeGroups.length === 0;
        activeGroups.forEach((group) => {
          const chip = document.createElement("span");
          chip.className = "siba-shop-active-filters__chip";
          chip.textContent = getFilterLabel(group);
          activeFilters.append(chip);
        });
      };
      const syncButtons = (group) => {
        var _a;
        const groupButtons = buttons.filter((candidate) => candidate.dataset.sibaFilterGroup === group);
        const activeButton = (_a = groupButtons.find((candidate) => candidate.dataset.sibaFilterValue === state[group])) != null ? _a : groupButtons.find((candidate) => candidate.dataset.sibaFilterValue === "all");
        groupButtons.forEach((candidate) => {
          candidate.classList.toggle("is-active", candidate === activeButton);
        });
      };
      const syncUrl = () => {
        const url = new URL(window.location.href);
        if (state.need === "all") url.searchParams.delete("need");
        else url.searchParams.set("need", state.need);
        if (state.format === "all") url.searchParams.delete("format");
        else url.searchParams.set("format", state.format);
        url.searchParams.delete("objetivo");
        window.history.replaceState({}, "", url);
      };
      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          var _a;
          const group = button.dataset.sibaFilterGroup;
          const value = (_a = button.dataset.sibaFilterValue) != null ? _a : "all";
          if (group !== "need" && group !== "format") return;
          state[group] = value;
          syncButtons(group);
          syncUrl();
          renderActiveFilters();
          render();
        });
      });
      syncButtons("need");
      syncButtons("format");
      syncUrl();
      renderActiveFilters();
      render();
    });
  };

  // src/scripts/presentation/faq.ts
  var initFaqTracking = () => {
    queryAll(".siba-quality-faq details").forEach((details) => {
      details.addEventListener("toggle", () => {
        var _a;
        if (!details.open) return;
        const summary = details.querySelector("summary");
        const question = (_a = summary == null ? void 0 : summary.textContent) == null ? void 0 : _a.replace(/\s+/g, " ").trim();
        if (question) rememberOpenedFaq(question);
      });
    });
  };

  // src/scripts/presentation/featured-products-carousel.ts
  var AUTOPLAY_DELAY_MS = 3e3;
  var AUTOPLAY_RESUME_DELAY_MS = 9e3;
  var REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
  var SETTLE_RETRY_DELAYS_MS = [120, 450, 1e3];
  var getCards = (track) => Array.from(track.children);
  var getCardLeft = (track, card) => card.offsetLeft - track.offsetLeft;
  var getActiveIndex = (track, cards) => {
    let activeIndex = 0;
    let activeDistance = Number.POSITIVE_INFINITY;
    cards.forEach((card, index) => {
      const distance = Math.abs(track.scrollLeft - getCardLeft(track, card));
      if (distance < activeDistance) {
        activeDistance = distance;
        activeIndex = index;
      }
    });
    return activeIndex;
  };
  var scrollToCard = (track, cards, index) => {
    const card = cards[index];
    if (!card) return;
    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    const nextLeft = getCardLeft(track, card);
    track.scrollTo({
      left: nextLeft >= maxScrollLeft - 2 ? maxScrollLeft : nextLeft,
      behavior: "smooth"
    });
  };
  var initFeaturedProductsCarousel = (root) => {
    const track = root.querySelector("[data-siba-featured-track]");
    const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
    if (!track) return;
    let autoplayId;
    let resumeId;
    let retryIds = [];
    let userPaused = false;
    let isEnabled = false;
    let resizeObserver;
    const hasOverflow = () => track.scrollWidth > track.clientWidth + 1;
    const stopAutoplay = () => {
      if (autoplayId !== void 0) {
        window.clearInterval(autoplayId);
        autoplayId = void 0;
      }
    };
    const clearResume = () => {
      if (resumeId !== void 0) {
        window.clearTimeout(resumeId);
        resumeId = void 0;
      }
    };
    const clearRetries = () => {
      retryIds.forEach((retryId) => window.clearTimeout(retryId));
      retryIds = [];
    };
    const startAutoplay = () => {
      stopAutoplay();
      const cards = getCards(track);
      if (cards.length < 2 || !hasOverflow()) {
        isEnabled = false;
        return;
      }
      isEnabled = true;
      if (userPaused || reducedMotion.matches) return;
      autoplayId = window.setInterval(() => {
        const currentCards = getCards(track);
        const maxScrollLeft = track.scrollWidth - track.clientWidth;
        if (track.scrollLeft >= maxScrollLeft - 2) {
          track.scrollTo({ left: 0, behavior: "smooth" });
          return;
        }
        const activeIndex = getActiveIndex(track, currentCards);
        const nextIndex = (activeIndex + 1) % currentCards.length;
        scrollToCard(track, currentCards, nextIndex);
      }, AUTOPLAY_DELAY_MS);
    };
    const scheduleSettledStart = () => {
      clearRetries();
      startAutoplay();
      retryIds = SETTLE_RETRY_DELAYS_MS.map((delay) => window.setTimeout(startAutoplay, delay));
    };
    const pauseForUser = () => {
      if (!isEnabled) return;
      userPaused = true;
      stopAutoplay();
      clearResume();
      resumeId = window.setTimeout(() => {
        userPaused = false;
        startAutoplay();
      }, AUTOPLAY_RESUME_DELAY_MS);
    };
    ["pointerdown", "touchstart", "wheel", "keydown"].forEach((eventName) => {
      track.addEventListener(eventName, pauseForUser, { passive: true });
    });
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        stopAutoplay();
        return;
      }
      startAutoplay();
    });
    window.addEventListener("load", scheduleSettledStart, { once: true });
    window.addEventListener("resize", scheduleSettledStart);
    reducedMotion.addEventListener("change", scheduleSettledStart);
    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(scheduleSettledStart);
      resizeObserver.observe(track);
    }
    root.addEventListener("shopify:section:unload", () => {
      stopAutoplay();
      clearResume();
      clearRetries();
      resizeObserver == null ? void 0 : resizeObserver.disconnect();
    });
    scheduleSettledStart();
  };
  var initFeaturedProductsCarousels = () => {
    document.querySelectorAll("[data-siba-featured-products]").forEach(initFeaturedProductsCarousel);
  };

  // src/scripts/presentation/header.ts
  var initHeader = () => {
    document.documentElement.classList.add("siba-js-ready");
  };

  // src/scripts/presentation/hero-carousel.ts
  function initHeroCarousels() {
    const carousels = document.querySelectorAll("[data-siba-hero-carousel]");
    carousels.forEach((carousel) => {
      var _a, _b, _c;
      const slides = Array.from(carousel.querySelectorAll("[data-siba-hero-slide]"));
      const root = (_a = carousel.closest(".siba-hero")) != null ? _a : carousel.parentElement;
      const controls = Array.from((_b = root == null ? void 0 : root.querySelectorAll("[data-siba-hero-control]")) != null ? _b : []);
      if (slides.length <= 1) {
        (_c = slides[0]) == null ? void 0 : _c.classList.add("is-active");
        return;
      }
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      let activeIndex = 0;
      const setActive = (nextIndex) => {
        var _a2, _b2, _c2, _d, _e, _f;
        (_a2 = slides[activeIndex]) == null ? void 0 : _a2.classList.remove("is-active");
        (_b2 = controls[activeIndex]) == null ? void 0 : _b2.classList.remove("is-active");
        (_c2 = controls[activeIndex]) == null ? void 0 : _c2.setAttribute("aria-current", "false");
        activeIndex = nextIndex % slides.length;
        (_d = slides[activeIndex]) == null ? void 0 : _d.classList.add("is-active");
        (_e = controls[activeIndex]) == null ? void 0 : _e.classList.add("is-active");
        (_f = controls[activeIndex]) == null ? void 0 : _f.setAttribute("aria-current", "true");
      };
      carousel.classList.add("is-ready");
      setActive(0);
      controls.forEach((control) => {
        control.addEventListener("click", () => {
          var _a2;
          const nextIndex = Number((_a2 = control.dataset.sibaHeroControl) != null ? _a2 : 0);
          setActive(Number.isNaN(nextIndex) ? 0 : nextIndex);
        });
      });
      if (reduceMotion.matches) {
        return;
      }
      window.setInterval(() => {
        setActive(activeIndex + 1);
      }, 5200);
    });
  }

  // src/scripts/presentation/objectives-carousel.ts
  var AUTOPLAY_DELAY_MS2 = 4e3;
  var AUTOPLAY_RESUME_DELAY_MS2 = 9e3;
  var MOBILE_QUERY = "(max-width: 749px)";
  var REDUCED_MOTION_QUERY2 = "(prefers-reduced-motion: reduce)";
  var scrollToSlide = (track, slides, index) => {
    const slide = slides[index];
    if (!slide) {
      return;
    }
    track.scrollTo({
      left: slide.offsetLeft - track.offsetLeft,
      behavior: "smooth"
    });
  };
  var getActiveIndex2 = (track, slides) => {
    const currentLeft = track.scrollLeft;
    let activeIndex = 0;
    let activeDistance = Number.POSITIVE_INFINITY;
    slides.forEach((slide, index) => {
      const distance = Math.abs(currentLeft - (slide.offsetLeft - track.offsetLeft));
      if (distance < activeDistance) {
        activeDistance = distance;
        activeIndex = index;
      }
    });
    return activeIndex;
  };
  var updateDots = (dots, activeIndex) => {
    dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle("is-active", isActive);
      if (isActive) {
        dot.setAttribute("aria-current", "true");
      } else {
        dot.removeAttribute("aria-current");
      }
    });
  };
  var initObjectivesCarousel = (root) => {
    const track = root.querySelector("[data-siba-objectives-track]");
    const slides = Array.from(root.querySelectorAll("[data-siba-objectives-slide]"));
    const dots = Array.from(root.querySelectorAll("[data-siba-objectives-dot]"));
    const mobileMedia = window.matchMedia(MOBILE_QUERY);
    const reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY2);
    if (!track || slides.length < 2 || dots.length === 0) {
      return;
    }
    let activeIndex = 0;
    let autoplayId;
    let resumeId;
    let userPaused = false;
    let scrollFrame;
    const setActiveFromScroll = () => {
      activeIndex = getActiveIndex2(track, slides);
      updateDots(dots, activeIndex);
    };
    const stopAutoplay = () => {
      if (autoplayId !== void 0) {
        window.clearInterval(autoplayId);
        autoplayId = void 0;
      }
    };
    const clearResume = () => {
      if (resumeId !== void 0) {
        window.clearTimeout(resumeId);
        resumeId = void 0;
      }
    };
    const startAutoplay = () => {
      stopAutoplay();
      if (userPaused || !mobileMedia.matches || reducedMotionMedia.matches) {
        return;
      }
      autoplayId = window.setInterval(() => {
        const nextIndex = (activeIndex + 1) % slides.length;
        scrollToSlide(track, slides, nextIndex);
      }, AUTOPLAY_DELAY_MS2);
    };
    const pauseForUser = () => {
      userPaused = true;
      stopAutoplay();
      clearResume();
      resumeId = window.setTimeout(() => {
        userPaused = false;
        startAutoplay();
      }, AUTOPLAY_RESUME_DELAY_MS2);
    };
    dots.forEach((dot, index) => {
      dot.addEventListener("click", () => {
        pauseForUser();
        scrollToSlide(track, slides, index);
      });
    });
    ["pointerdown", "touchstart", "wheel", "keydown"].forEach((eventName) => {
      track.addEventListener(eventName, pauseForUser, { passive: true });
    });
    track.addEventListener(
      "scroll",
      () => {
        if (scrollFrame !== void 0) {
          window.cancelAnimationFrame(scrollFrame);
        }
        scrollFrame = window.requestAnimationFrame(setActiveFromScroll);
      },
      { passive: true }
    );
    mobileMedia.addEventListener("change", () => {
      userPaused = false;
      clearResume();
      startAutoplay();
    });
    reducedMotionMedia.addEventListener("change", () => {
      userPaused = false;
      clearResume();
      startAutoplay();
    });
    setActiveFromScroll();
    startAutoplay();
  };
  var initObjectivesCarousels = () => {
    document.querySelectorAll("[data-siba-objectives]").forEach(initObjectivesCarousel);
  };

  // src/scripts/presentation/product-description-accordions.ts
  var markerPattern = /^\s*#(?:[ \t]*(.*))?$/s;
  var getMarker = (text) => {
    var _a, _b, _c;
    const lines = text.trim().split(/\r?\n/);
    const firstLine = (_a = lines[0]) != null ? _a : "";
    const match = firstLine.match(markerPattern);
    if (!match) return { type: "content" };
    const title = (_c = (_b = match[1]) == null ? void 0 : _b.trim()) != null ? _c : "";
    if (!title) return { type: "stop" };
    return {
      type: "heading",
      title,
      remainder: lines.slice(1).join("\n").trim()
    };
  };
  var createAccordion = (title) => {
    const details = document.createElement("details");
    details.className = "siba-description-accordion";
    const summary = document.createElement("summary");
    summary.textContent = title;
    const content = document.createElement("div");
    content.className = "siba-description-accordion__content";
    details.append(summary, content);
    return { details, content };
  };
  var createRemainderNode = (source, remainder) => {
    if (!remainder) return null;
    if (source.nodeType === Node.TEXT_NODE) {
      return document.createTextNode(remainder);
    }
    if (source instanceof HTMLElement) {
      const clone = source.cloneNode(false);
      clone.textContent = remainder;
      return clone;
    }
    return document.createTextNode(remainder);
  };
  var getDefaultLocalePath = () => {
    const match = window.location.pathname.match(/^\/[a-z]{2}(?:-[a-z]{2})?(\/.*)$/i);
    if (!(match == null ? void 0 : match[1])) return null;
    return match[1];
  };
  var hasHashHeadings = (element) => Array.from(element.childNodes).some((node) => {
    var _a;
    return getMarker((_a = node.textContent) != null ? _a : "").type === "heading";
  });
  var isVisuallyEmpty = (element) => {
    var _a;
    return !((_a = element.textContent) == null ? void 0 : _a.replace(/\s+/g, " ").trim());
  };
  var shouldHydrateFromDefaultLocale = (description) => description.dataset.sibaGeneratedDescription === "true" || isVisuallyEmpty(description);
  var buildFallbackDescription = () => {
    var _a, _b, _c, _d, _e, _f;
    const summary = document.querySelector(".siba-product__media-summary");
    if (!summary) return null;
    const description = document.createElement("div");
    description.className = "product__description rte quick-add-hidden";
    description.dataset.sibaGeneratedDescription = "true";
    const productTitle = (_c = (_b = (_a = document.querySelector(".product__title h1")) == null ? void 0 : _a.textContent) == null ? void 0 : _b.trim()) != null ? _c : "This product";
    const intro = document.createElement("p");
    intro.textContent = `${productTitle} is designed to support a simple daily wellness routine with clean, carefully selected ingredients.`;
    description.append(intro);
    const benefits = queryAll(".siba-product__benefits > span, .siba-product__benefit", summary).filter(
      (item) => {
        var _a2;
        return Boolean((_a2 = item.textContent) == null ? void 0 : _a2.trim());
      }
    );
    if (benefits.length > 0) {
      const accordion = createAccordion(`Why people choose ${productTitle}`);
      benefits.forEach((benefit) => {
        var _a2, _b2;
        const paragraph = document.createElement("p");
        paragraph.textContent = (_b2 = (_a2 = benefit.textContent) == null ? void 0 : _a2.replace(/\s+/g, " ").trim()) != null ? _b2 : "";
        accordion.content.append(paragraph);
      });
      description.append(accordion.details);
    }
    const ingredients = summary.querySelector(".siba-product__ingredients");
    const ingredientContent = ingredients == null ? void 0 : ingredients.querySelector(
      ".siba-product__ingredient-copy, .siba-product__ingredient-chips"
    );
    if (ingredientContent && ((_d = ingredientContent.textContent) == null ? void 0 : _d.trim())) {
      const title = ((_f = (_e = ingredients == null ? void 0 : ingredients.querySelector("h3")) == null ? void 0 : _e.textContent) == null ? void 0 : _f.trim()) || "Main ingredients";
      const accordion = createAccordion(title);
      accordion.content.innerHTML = ingredientContent.innerHTML;
      description.append(accordion.details);
    }
    const disclaimer = document.createElement("p");
    const emphasis = document.createElement("em");
    emphasis.textContent = "This product is not intended to diagnose, treat, cure, or prevent any disease.";
    disclaimer.append(emphasis);
    description.append(disclaimer);
    return description;
  };
  var ensureDescriptionBlock = (root = document) => {
    const panel = document.querySelector(".siba-product__panel");
    if (!panel) return;
    const existingDescription = panel.querySelector(".product__description");
    if (existingDescription && !isVisuallyEmpty(existingDescription)) return;
    const fallback = buildFallbackDescription();
    if (!fallback) return;
    if (existingDescription) {
      existingDescription.replaceWith(fallback);
      return;
    }
    const share = panel.querySelector("share-button");
    panel.insertBefore(fallback, share);
  };
  var processDescriptions = () => {
    ensureDescriptionBlock();
    queryAll(".siba-product__panel .product__description").forEach((description) => {
      if (isVisuallyEmpty(description)) {
        ensureDescriptionBlock();
      }
      const targetDescription = document.querySelector(".siba-product__panel .product__description");
      if (!targetDescription) return;
      const parsed = parseDescription(targetDescription);
      if (!parsed && !hasHashHeadings(targetDescription) && shouldHydrateFromDefaultLocale(targetDescription)) {
        void hydrateFromDefaultLocale(targetDescription);
      }
    });
  };
  var parseDescription = (description) => {
    if (description.dataset.sibaDescriptionParsed === "true") return true;
    const nodes = Array.from(description.childNodes).filter((node) => {
      var _a;
      if (node.nodeType !== Node.TEXT_NODE) return true;
      return Boolean((_a = node.textContent) == null ? void 0 : _a.trim());
    });
    const hasHeadingMarker = nodes.some((node) => {
      var _a, _b;
      return getMarker((_b = (_a = node.textContent) == null ? void 0 : _a.trim()) != null ? _b : "").type === "heading";
    });
    if (!hasHeadingMarker) return false;
    let hasAccordion = false;
    let stopped = false;
    let activeContent = null;
    const fragment = document.createDocumentFragment();
    nodes.forEach((node) => {
      var _a, _b;
      const text = (_b = (_a = node.textContent) == null ? void 0 : _a.trim()) != null ? _b : "";
      const marker = getMarker(text);
      if (!stopped && marker.type === "stop") {
        stopped = true;
        activeContent = null;
        return;
      }
      if (!stopped && marker.type === "heading") {
        const accordion = createAccordion(marker.title);
        fragment.append(accordion.details);
        activeContent = accordion.content;
        hasAccordion = true;
        const remainderNode = createRemainderNode(node, marker.remainder);
        if (remainderNode) activeContent.append(remainderNode);
        return;
      }
      if (!stopped && activeContent) {
        activeContent.append(node);
        return;
      }
      fragment.append(node);
    });
    if (!hasAccordion) return false;
    description.replaceChildren(fragment);
    description.dataset.sibaDescriptionParsed = "true";
    return true;
  };
  var hydrateFromDefaultLocale = async (description) => {
    if (description.dataset.sibaDefaultLocaleLoaded === "true") return;
    const defaultPath = getDefaultLocalePath();
    if (!defaultPath || defaultPath === window.location.pathname) return;
    description.dataset.sibaDefaultLocaleLoaded = "true";
    try {
      const response = await fetch(`${defaultPath}${window.location.search}`, {
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });
      if (!response.ok) return;
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, "text/html");
      const defaultDescription = documentFragment.querySelector(
        ".siba-product__panel .product__description"
      );
      if (!defaultDescription || !hasHashHeadings(defaultDescription)) return;
      description.innerHTML = defaultDescription.innerHTML;
      parseDescription(description);
    } catch (e) {
    }
  };
  var initProductDescriptionAccordions = () => {
    processDescriptions();
    document.addEventListener("product-info:loaded", processDescriptions);
    document.addEventListener("shopify:section:load", processDescriptions);
    const panel = document.querySelector(".siba-product__panel");
    if (!panel) return;
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        const description = document.querySelector(".siba-product__panel .product__description");
        if (!description || isVisuallyEmpty(description)) {
          processDescriptions();
        }
      });
    });
    observer.observe(panel, {
      childList: true,
      subtree: true
    });
  };

  // src/scripts/presentation/product-media-gallery.ts
  var initProductMediaGallery = () => {
    queryAll(".siba-product__media-card media-gallery").forEach((gallery) => {
      const thumbnails = queryAll("[data-target]", gallery);
      thumbnails.forEach((thumbnail) => {
        const button = thumbnail.querySelector("button");
        if (!button) return;
        thumbnail.addEventListener("mouseenter", () => {
          if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
          button.click();
        });
      });
    });
  };

  // src/scripts/presentation/product-media-modal.ts
  var getActiveIndex3 = (items) => {
    const activeIndex = items.findIndex((item) => item.classList.contains("active"));
    return activeIndex >= 0 ? activeIndex : 0;
  };
  var getItems = (modal) => queryAll(".product-media-modal__content > [data-media-id]", modal);
  var updateCounter = (modal, index, total) => {
    const counter = modal.querySelector("[data-siba-product-modal-counter]");
    if (counter) counter.textContent = `${index + 1} / ${total}`;
    const prevButton = modal.querySelector("[data-siba-product-modal-prev]");
    const nextButton = modal.querySelector("[data-siba-product-modal-next]");
    const isFirst = index <= 0;
    const isLast = index >= total - 1;
    if (prevButton) {
      prevButton.disabled = isFirst;
      prevButton.hidden = isFirst;
      prevButton.setAttribute("aria-hidden", String(isFirst));
    }
    if (nextButton) {
      nextButton.disabled = isLast;
      nextButton.hidden = isLast;
      nextButton.setAttribute("aria-hidden", String(isLast));
    }
  };
  var loadDeferredMedia = (item) => {
    var _a;
    if (item.nodeName === "DEFERRED-MEDIA") {
      (_a = item.loadContent) == null ? void 0 : _a.call(item);
    }
  };
  var showMediaAt = (modal, nextIndex) => {
    var _a;
    const items = getItems(modal);
    if (!items.length) return;
    const boundedIndex = Math.max(0, Math.min(nextIndex, items.length - 1));
    items.forEach((item, index) => {
      item.classList.toggle("active", index === boundedIndex);
    });
    (_a = window.pauseAllMedia) == null ? void 0 : _a.call(window);
    loadDeferredMedia(items[boundedIndex]);
    updateCounter(modal, boundedIndex, items.length);
  };
  var syncModalState = (modal) => {
    const items = getItems(modal);
    if (!items.length) return;
    const activeIndex = getActiveIndex3(items);
    const content = modal.querySelector(".product-media-modal__content");
    if (content) {
      content.scrollLeft = 0;
      content.scrollTop = 0;
    }
    updateCounter(modal, activeIndex, items.length);
  };
  var initProductMediaModal = () => {
    queryAll("product-modal").forEach((modal) => {
      var _a, _b;
      const items = getItems(modal);
      if (!items.length) return;
      updateCounter(modal, getActiveIndex3(items), items.length);
      const controls = queryAll(
        "[data-siba-product-modal-prev], [data-siba-product-modal-next], [data-siba-product-modal-counter]",
        modal
      );
      controls.forEach((control) => {
        control.addEventListener("pointerup", (event) => event.stopPropagation());
      });
      (_a = modal.querySelector("[data-siba-product-modal-prev]")) == null ? void 0 : _a.addEventListener("click", (event) => {
        event.stopPropagation();
        const currentItems = getItems(modal);
        showMediaAt(modal, getActiveIndex3(currentItems) - 1);
      });
      (_b = modal.querySelector("[data-siba-product-modal-next]")) == null ? void 0 : _b.addEventListener("click", (event) => {
        event.stopPropagation();
        const currentItems = getItems(modal);
        showMediaAt(modal, getActiveIndex3(currentItems) + 1);
      });
      modal.addEventListener("keyup", (event) => {
        if (!(event instanceof KeyboardEvent)) return;
        const currentItems = getItems(modal);
        if (event.key === "ArrowLeft") showMediaAt(modal, getActiveIndex3(currentItems) - 1);
        if (event.key === "ArrowRight") showMediaAt(modal, getActiveIndex3(currentItems) + 1);
      });
      modal.addEventListener("click", (event) => {
        if (!(event.target instanceof Element)) return;
        if (!event.target.closest(".product-media-modal__content > [data-media-id]")) return;
        const currentItems = getItems(modal);
        updateCounter(modal, getActiveIndex3(currentItems), currentItems.length);
      });
      new MutationObserver(() => {
        if (!modal.hasAttribute("open")) return;
        requestAnimationFrame(() => syncModalState(modal));
      }).observe(modal, { attributes: true, attributeFilter: ["open"] });
    });
  };

  // src/scripts/presentation/whatsapp.ts
  var initWhatsappTracking = () => {
    queryAll(".siba-whatsapp-float").forEach((link) => {
      link.addEventListener("click", countWhatsappClick);
    });
  };

  // src/scripts/app.ts
  initObservability();
  onReady(() => {
    rememberCurrentPath();
    initHeader();
    initHeroCarousels();
    initFeaturedProductsCarousels();
    initObjectivesCarousels();
    initSibaCartForms();
    initCollectionFilters();
    initProductDescriptionAccordions();
    initProductMediaGallery();
    initProductMediaModal();
    initFaqTracking();
    initWhatsappTracking();
    initAiChat();
  });
})();
